import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiTrendingUp, FiTrendingDown, FiDollarSign, FiActivity,
  FiSearch, FiDownload, FiPrinter, FiChevronDown, FiChevronRight,
  FiCalendar, FiFilter, FiRefreshCw, FiBarChart2, FiExternalLink
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { recordReads } from '../../utils/readTelemetry';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import useReportMemory from '../../hooks/useReportMemory';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area, LineChart, Line
} from 'recharts';
import { addPrintBrand } from '../../utils/printBranding';

// ---- Constants ----

const CHART_COLORS = {
  primary: '#800020',
  secondary: '#A0153E',
  tertiary: '#5D0017',
  accent1: '#C4A484',
  accent2: '#8B7355',
  accent3: '#D4AF37'
};

const CASH_BANK_PREFIX = ['11101', '11102'];

// Map tranTypeCode to cash flow category
const TRAN_TYPE_CATEGORIES = {
  // Operating Activities
  CR: 'operating_inflow',    // Customer Receipt
  BR: 'operating_inflow',    // Bank Receipt
  CP: 'operating_outflow',   // Cash Payment
  BP: 'operating_outflow',   // Bank Payment
  SI: 'operating_other',     // Sales Invoice (cash entries)
  NSI: 'operating_other',    // Non-Tax Sales Invoice (cash entries)
  PI: 'operating_other',     // Purchase Invoice (cash entries)
  NPI: 'operating_other',    // Non-Tax Purchase Invoice (cash entries)
  SR: 'operating_other',     // Sales Return (cash entries)
  NSR: 'operating_other',    // Non-Tax Sales Return (cash entries)
  PR: 'operating_other',     // Purchase Return (cash entries)
  NPR: 'operating_other',    // Non-Tax Purchase Return (cash entries)
  JV: 'operating_other',     // Journal Voucher
  // Investing
  FA: 'investing',           // Fixed Asset
  // Financing
  EQ: 'financing',           // Equity
  LN: 'financing'            // Loan
};

// ---- Helpers ----

const parseFirestoreDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal.toDate) return dateVal.toDate();
  if (dateVal.seconds) return new Date(dateVal.seconds * 1000);
  if (typeof dateVal === 'string') return new Date(dateVal);
  if (dateVal instanceof Date) return dateVal;
  return null;
};

const formatDateForInput = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const fmtAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const fmtDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getMonthLabel = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-PK', { month: 'short', year: '2-digit' });
};

const getQuarterLabel = (dateStr) => {
  const d = new Date(dateStr);
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${q} ${d.getFullYear().toString().slice(-2)}`;
};

// Get period key for grouping
const getPeriodKey = (dateStr, periodType) => {
  const d = new Date(dateStr);
  if (periodType === 'quarterly') {
    const q = Math.ceil((d.getMonth() + 1) / 3);
    return `${d.getFullYear()}-Q${q}`;
  }
  // monthly
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${d.getFullYear()}-${m}`;
};

const getPeriodLabel = (periodKey, periodType) => {
  if (periodType === 'quarterly') {
    const [yr, q] = periodKey.split('-');
    return `${q} ${yr.slice(-2)}`;
  }
  const [yr, m] = periodKey.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${yr.slice(-2)}`;
};

// Generate period keys between two dates
const generatePeriodKeys = (startDate, endDate, periodType) => {
  const keys = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (periodType === 'quarterly') {
    let current = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
    while (current <= end) {
      const q = Math.ceil((current.getMonth() + 1) / 3);
      keys.push(`${current.getFullYear()}-Q${q}`);
      current.setMonth(current.getMonth() + 3);
    }
  } else {
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    while (current <= end) {
      const m = (current.getMonth() + 1).toString().padStart(2, '0');
      keys.push(`${current.getFullYear()}-${m}`);
      current.setMonth(current.getMonth() + 1);
    }
  }
  return keys;
};

// Classify a transaction's cash flow category
const classifyTransaction = (tranTypeCode, description) => {
  const code = (tranTypeCode || '').toUpperCase();
  if (TRAN_TYPE_CATEGORIES[code]) return TRAN_TYPE_CATEGORIES[code];

  // Fallback: classify based on description keywords
  const desc = (description || '').toLowerCase();
  if (desc.includes('asset') || desc.includes('equipment') || desc.includes('machinery') || desc.includes('vehicle')) {
    return 'investing';
  }
  if (desc.includes('loan') || desc.includes('capital') || desc.includes('equity') || desc.includes('dividend')) {
    return 'financing';
  }
  return 'operating_other';
};

// ---- Component ----

const CashFlow = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('cash-flow');
  const navigate = useNavigate();

  // Data state
  const [accounts, setAccounts] = useState([]);
  const [allTransactions, setAllTransactions] = useState(() => savedMem?.allTransactions ?? []);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [viewMode, setViewMode] = useState(() => savedMem?.viewMode ?? 'summary');
  const [periodType, setPeriodType] = useState(() => savedMem?.periodType ?? 'monthly');
  const [showFilters, setShowFilters] = useState(() => savedMem?.showFilters ?? true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);
  const [expandedSections, setExpandedSections] = useState(() => savedMem?.expandedSections ?? {
    operating: true,
    investing: true,
    financing: true
  });

  // ============================================================
  // LOAD FINANCIAL YEARS
  // ============================================================
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
    } catch (err) {
      console.error('Error loading financial years:', err);
    }
  }, [activeCompany?.id]);

  // ============================================================
  // LOAD ACCOUNTS
  // ============================================================
  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const snapshot = await getDocs(accountsRef);
      recordReads(activeCompany.id, snapshot.size);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAccounts(list);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  }, [activeCompany?.id]);

  useEffect(() => {
    loadFinancialYears();
    loadAccounts();
  }, [loadFinancialYears, loadAccounts]);

  // Set defaults on FY load
  useEffect(() => {
    if (activeFinancialYear?.id && !selectedFYId) {
      setSelectedFYId(activeFinancialYear.id);
    }
  }, [activeFinancialYear, selectedFYId]);

  // Update date range when FY changes
  useEffect(() => {
    // Back-restore keeps the user's saved date range (fresh visit only).
    if (savedMem) return;
    if (!selectedFYId || financialYears.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) {
      const start = parseFirestoreDate(fy.startDate);
      const end = parseFirestoreDate(fy.endDate);
      const today = new Date();
      if (start) setDateFrom(fy.startDate && typeof fy.startDate === 'string' ? fy.startDate : formatDateForInput(start));
      if (end) {
        const effectiveEnd = end < today ? end : today;
        setDateTo(fy.endDate && typeof fy.endDate === 'string' ? fy.endDate : formatDateForInput(effectiveEnd));
      }
    }
  }, [selectedFYId, financialYears, savedMem]);

  // ============================================================
  // IDENTIFY CASH/BANK ACCOUNTS
  // ============================================================
  const cashBankAccountIds = useMemo(() => {
    return accounts
      .filter(acc => {
        const code = acc.code || '';
        return CASH_BANK_PREFIX.some(prefix => code.startsWith(prefix));
      })
      .map(acc => acc.id);
  }, [accounts]);

  const cashBankAccountMap = useMemo(() => {
    const map = {};
    accounts.forEach(acc => {
      const code = acc.code || '';
      if (CASH_BANK_PREFIX.some(prefix => code.startsWith(prefix))) {
        map[acc.id] = acc;
      }
    });
    return map;
  }, [accounts]);

  // ============================================================
  // FETCH TRANSACTIONS
  // ============================================================
  const fetchData = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId || cashBankAccountIds.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const q = query(
        subTransRef,
        where('financialYearId', '==', selectedFYId),
        where('status', 'in', ['Posted', 'Draft'])
      );
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const allTrans = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => {
        // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
        if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Filter only cash/bank account transactions
      const cashTrans = allTrans.filter(t => cashBankAccountIds.includes(t.accountId));
      setAllTransactions(cashTrans);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load cash flow data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeCompany?.id, selectedFYId, cashBankAccountIds, activeBranch]);

  // ============================================================
  // GENERATE REPORT
  // ============================================================
  // The heavy subTransactions fetch only runs when the user clicks
  // Generate. The small accounts/FY lookups above stay automatic.
  const handleGenerateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    setHasGenerated(true);
    await fetchData();
  };

  // Remember this view (filters + generated rows) so Back restores it with 0
  // re-reads. The date/view/period filters are display-only over already-loaded
  // data, so we keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasGenerated) return;
    saveMemory({
      allTransactions,
      hasGenerated: true,
      selectedFYId, dateFrom, dateTo, viewMode, periodType, showFilters,
      expandedSections
    });
  }, [hasGenerated, allTransactions, selectedFYId, dateFrom, dateTo, viewMode,
      periodType, showFilters, expandedSections, saveMemory]);

  // ============================================================
  // COMPUTE CASH FLOW DATA
  // ============================================================
  const cashFlowData = useMemo(() => {
    if (allTransactions.length === 0 || !dateFrom || !dateTo) {
      return {
        openingBalance: 0,
        closingBalance: 0,
        totalInflows: 0,
        totalOutflows: 0,
        operating: { inflows: [], outflows: [], other: [], netCash: 0 },
        investing: { items: [], netCash: 0 },
        financing: { items: [], netCash: 0 },
        netChange: 0,
        periodData: [],
        detailedItems: []
      };
    }

    const fromDate = dateFrom;
    const toDate = dateTo;

    // Separate transactions: before dateFrom (opening) vs within range
    const beforeRange = [];
    const inRange = [];

    allTransactions.forEach(t => {
      const dd = t.docDate || '';
      if (dd < fromDate) {
        beforeRange.push(t);
      } else if (dd >= fromDate && dd <= toDate) {
        inRange.push(t);
      }
    });

    // Calculate opening balance (net of all cash/bank transactions before dateFrom)
    // For asset accounts (cash/bank): debit increases balance, credit decreases
    let openingBalance = 0;
    beforeRange.forEach(t => {
      openingBalance += (t.debitAmount || 0) - (t.creditAmount || 0);
    });

    // Categorize in-range transactions
    let totalInflows = 0;
    let totalOutflows = 0;

    // Operating
    const operatingInflows = {};  // Group by tranTypeCode
    const operatingOutflows = {};
    const operatingOther = {};

    // Investing
    const investingItems = {};

    // Financing
    const financingItems = {};

    // Period-wise data
    const periodMap = {};
    const periodKeys = generatePeriodKeys(fromDate, toDate, periodType);
    periodKeys.forEach(key => {
      periodMap[key] = { inflows: 0, outflows: 0, balance: 0 };
    });

    // Detailed items for detailed view
    const detailedItems = [];

    inRange.forEach(t => {
      const debit = t.debitAmount || 0;
      const credit = t.creditAmount || 0;
      const isInflow = debit > 0;
      const isOutflow = credit > 0;
      const amount = isInflow ? debit : credit;
      const tranType = (t.tranTypeCode || 'JV').toUpperCase();
      const category = classifyTransaction(tranType, t.description);
      const periodKey = getPeriodKey(t.docDate || dateFrom, periodType);

      if (isInflow) totalInflows += debit;
      if (isOutflow) totalOutflows += credit;

      // Period tracking
      if (periodMap[periodKey]) {
        if (isInflow) periodMap[periodKey].inflows += debit;
        if (isOutflow) periodMap[periodKey].outflows += credit;
      }

      // Detailed item
      detailedItems.push({
        id: t.id,
        date: t.docDate,
        description: t.description || '',
        tranTypeCode: tranType,
        transactionId: t.transactionId || '',
        inflow: isInflow ? debit : 0,
        outflow: isOutflow ? credit : 0,
        category,
        accountId: t.accountId,
        accountName: cashBankAccountMap[t.accountId]?.name || ''
      });

      // Category grouping
      const label = getTransactionLabel(tranType, isInflow);

      if (category === 'operating_inflow' || (category === 'operating_other' && isInflow)) {
        if (!operatingInflows[label]) operatingInflows[label] = { label, amount: 0, tranType, count: 0 };
        operatingInflows[label].amount += debit;
        operatingInflows[label].count += 1;
      } else if (category === 'operating_outflow' || (category === 'operating_other' && isOutflow)) {
        if (!operatingOutflows[label]) operatingOutflows[label] = { label, amount: 0, tranType, count: 0 };
        operatingOutflows[label].amount += credit;
        operatingOutflows[label].count += 1;
      } else if (category === 'investing') {
        const invLabel = isInflow ? `${label} (Proceeds)` : `${label} (Purchases)`;
        if (!investingItems[invLabel]) investingItems[invLabel] = { label: invLabel, amount: 0, isInflow, count: 0 };
        investingItems[invLabel].amount += isInflow ? debit : -credit;
        investingItems[invLabel].count += 1;
      } else if (category === 'financing') {
        const finLabel = isInflow ? `${label} (Received)` : `${label} (Paid)`;
        if (!financingItems[finLabel]) financingItems[finLabel] = { label: finLabel, amount: 0, isInflow, count: 0 };
        financingItems[finLabel].amount += isInflow ? debit : -credit;
        financingItems[finLabel].count += 1;
      }
    });

    // Build period data with running balance
    let runningBalance = openingBalance;
    const periodData = periodKeys.map(key => {
      const pd = periodMap[key] || { inflows: 0, outflows: 0 };
      runningBalance += pd.inflows - pd.outflows;
      return {
        period: getPeriodLabel(key, periodType),
        periodKey: key,
        inflows: pd.inflows,
        outflows: pd.outflows,
        netFlow: pd.inflows - pd.outflows,
        balance: runningBalance
      };
    });

    const closingBalance = openingBalance + totalInflows - totalOutflows;

    const operatingInflowList = Object.values(operatingInflows).sort((a, b) => b.amount - a.amount);
    const operatingOutflowList = Object.values(operatingOutflows).sort((a, b) => b.amount - a.amount);
    const investingList = Object.values(investingItems).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    const financingList = Object.values(financingItems).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

    const operatingNet = operatingInflowList.reduce((s, i) => s + i.amount, 0)
      - operatingOutflowList.reduce((s, i) => s + i.amount, 0);
    const investingNet = investingList.reduce((s, i) => s + i.amount, 0);
    const financingNet = financingList.reduce((s, i) => s + i.amount, 0);

    return {
      openingBalance,
      closingBalance,
      totalInflows,
      totalOutflows,
      operating: {
        inflows: operatingInflowList,
        outflows: operatingOutflowList,
        netCash: operatingNet
      },
      investing: {
        items: investingList,
        netCash: investingNet
      },
      financing: {
        items: financingList,
        netCash: financingNet
      },
      netChange: closingBalance - openingBalance,
      periodData,
      detailedItems: detailedItems.sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    };
  }, [allTransactions, dateFrom, dateTo, periodType, cashBankAccountMap]);

  // ============================================================
  // CHART DATA
  // ============================================================
  const balanceTrendData = useMemo(() => {
    return cashFlowData.periodData;
  }, [cashFlowData.periodData]);

  const inflowOutflowData = useMemo(() => {
    return cashFlowData.periodData.map(pd => ({
      period: pd.period,
      Inflows: pd.inflows,
      Outflows: pd.outflows
    }));
  }, [cashFlowData.periodData]);

  const waterfallData = useMemo(() => {
    const data = [
      {
        name: 'Opening',
        value: cashFlowData.openingBalance,
        fill: CHART_COLORS.primary,
        base: 0
      },
      {
        name: 'Operating',
        value: cashFlowData.operating.netCash,
        fill: cashFlowData.operating.netCash >= 0 ? '#28A745' : '#DC3545',
        base: cashFlowData.openingBalance
      },
      {
        name: 'Investing',
        value: cashFlowData.investing.netCash,
        fill: cashFlowData.investing.netCash >= 0 ? '#28A745' : '#DC3545',
        base: cashFlowData.openingBalance + cashFlowData.operating.netCash
      },
      {
        name: 'Financing',
        value: cashFlowData.financing.netCash,
        fill: cashFlowData.financing.netCash >= 0 ? '#28A745' : '#DC3545',
        base: cashFlowData.openingBalance + cashFlowData.operating.netCash + cashFlowData.investing.netCash
      },
      {
        name: 'Closing',
        value: cashFlowData.closingBalance,
        fill: CHART_COLORS.tertiary,
        base: 0
      }
    ];
    return data;
  }, [cashFlowData]);

  // ============================================================
  // HELPERS
  // ============================================================
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleRefresh = () => {
    handleGenerateReport();
  };

  // ============================================================
  // CSV EXPORT
  // ============================================================
  const handleExport = useCallback(() => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const lines = [];
    lines.push(`Cash Flow Statement`);
    lines.push(`Company: ${activeCompany?.companyName || ''}`);
    lines.push(`Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || selectedFY?.name || ''}`);
    lines.push(`Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)}`);
    lines.push('');

    lines.push('Category,Particulars,Amount');
    lines.push('');

    lines.push(`Opening,Opening Cash & Bank Balance,${fmtAmount(cashFlowData.openingBalance)}`);
    lines.push('');

    // Operating
    lines.push('OPERATING ACTIVITIES,,');
    cashFlowData.operating.inflows.forEach(item => {
      lines.push(`Operating Inflow,"${item.label}",${fmtAmount(item.amount)}`);
    });
    cashFlowData.operating.outflows.forEach(item => {
      lines.push(`Operating Outflow,"${item.label}",${fmtAmount(-item.amount)}`);
    });
    lines.push(`Operating Total,Net Cash from Operations,${fmtAmount(cashFlowData.operating.netCash)}`);
    lines.push('');

    // Investing
    lines.push('INVESTING ACTIVITIES,,');
    cashFlowData.investing.items.forEach(item => {
      lines.push(`Investing,"${item.label}",${fmtAmount(item.amount)}`);
    });
    lines.push(`Investing Total,Net Cash from Investing,${fmtAmount(cashFlowData.investing.netCash)}`);
    lines.push('');

    // Financing
    lines.push('FINANCING ACTIVITIES,,');
    cashFlowData.financing.items.forEach(item => {
      lines.push(`Financing,"${item.label}",${fmtAmount(item.amount)}`);
    });
    lines.push(`Financing Total,Net Cash from Financing,${fmtAmount(cashFlowData.financing.netCash)}`);
    lines.push('');

    lines.push(`Net Change,Net Change in Cash,${fmtAmount(cashFlowData.netChange)}`);
    lines.push(`Closing,Closing Cash & Bank Balance,${fmtAmount(cashFlowData.closingBalance)}`);

    if (viewMode === 'detailed') {
      lines.push('');
      lines.push('DETAILED TRANSACTIONS');
      lines.push('Date,Type,Description,Account,Inflow,Outflow');
      cashFlowData.detailedItems.forEach(item => {
        lines.push(`${item.date},"${item.tranTypeCode}","${item.description.replace(/"/g, '""')}","${item.accountName}",${fmtAmount(item.inflow)},${fmtAmount(item.outflow)}`);
      });
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Cash_Flow_${dateFrom}_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [activeCompany, dateFrom, dateTo, selectedFYId, financialYears, cashFlowData, viewMode]);

  // ============================================================
  // PRINT
  // ============================================================
  const handlePrint = useCallback(() => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    const buildCategoryRows = (items, isOutflow = false) => {
      return items.map(item => {
        const amt = isOutflow ? -item.amount : item.amount;
        return `<tr class="detail-row">
          <td style="padding-left:40px">${item.label}</td>
          <td class="amount">${fmtAmount(amt)}</td>
          <td></td>
        </tr>`;
      }).join('');
    };

    const buildInvestFinanceRows = (items) => {
      return items.map(item => {
        return `<tr class="detail-row">
          <td style="padding-left:40px">${item.label}</td>
          <td class="amount">${fmtAmount(item.amount)}</td>
          <td></td>
        </tr>`;
      }).join('');
    };

    const w = window.open('', '_blank');
    if (!w) return;

    w.document.write(`<html><head><title>Cash Flow Statement</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        .header{background:#800020;color:white;padding:20px;border-radius:8px;margin-bottom:20px}
        .header h1{margin:0;font-size:22px}
        .header .meta{font-size:12px;opacity:0.9;margin-top:6px}
        .summary-cards{display:flex;gap:12px;margin-bottom:20px;flex-wrap:wrap}
        .summary-card{flex:1;min-width:140px;padding:12px;background:#f8f9fa;border-left:4px solid #800020;border-radius:4px}
        .summary-card .label{font-size:11px;color:#666;margin-bottom:4px}
        .summary-card .value{font-size:18px;font-weight:700}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#f5f5f5;padding:10px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #800020}
        th.amount{text-align:right}
        td{padding:8px 10px;border-bottom:1px solid #eee}
        td.amount{text-align:right;font-family:'Consolas',monospace;font-size:12px}
        .section-header td{font-weight:700;font-size:13px;background:#f9f5f6;color:#800020;border-bottom:2px solid #e0d0d5;padding:12px 10px}
        .total-row td{font-weight:600;border-top:1px solid #ccc;background:#f5f0f1}
        .grand-total td{font-weight:700;font-size:14px;background:#800020;color:white}
        .detail-row td{font-size:12px;color:#444}
        .positive{color:#28A745}
        .negative{color:#DC3545}
        @media print{body{margin:15px}.header,.summary-card,.grand-total td{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      </style></head><body>
      <div class="header">
        <h1>Cash Flow Statement</h1>
        <div class="meta">
          ${activeCompany?.companyName || 'Company'} |
          Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || selectedFY?.name || ''} |
          Period: ${fmtDate(dateFrom)} to ${fmtDate(dateTo)}
        </div>
      </div>
      <div class="summary-cards">
        <div class="summary-card"><div class="label">Opening Balance</div><div class="value">${fmtAmount(cashFlowData.openingBalance)}</div></div>
        <div class="summary-card" style="border-color:#28A745"><div class="label">Total Inflows</div><div class="value positive">${fmtAmount(cashFlowData.totalInflows)}</div></div>
        <div class="summary-card" style="border-color:#DC3545"><div class="label">Total Outflows</div><div class="value negative">(${fmtAmount(cashFlowData.totalOutflows)})</div></div>
        <div class="summary-card" style="border-color:#5D0017"><div class="label">Closing Balance</div><div class="value">${fmtAmount(cashFlowData.closingBalance)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Particulars</th>
          <th class="amount" style="width:150px">Amount</th>
          <th class="amount" style="width:150px">Net</th>
        </tr></thead>
        <tbody>
          <tr class="grand-total">
            <td>Opening Cash & Bank Balance</td>
            <td></td>
            <td class="amount">${fmtAmount(cashFlowData.openingBalance)}</td>
          </tr>

          <tr class="section-header"><td colspan="3">OPERATING ACTIVITIES</td></tr>
          ${buildCategoryRows(cashFlowData.operating.inflows, false)}
          ${buildCategoryRows(cashFlowData.operating.outflows, true)}
          <tr class="total-row">
            <td>Net Cash from Operations</td>
            <td></td>
            <td class="amount ${cashFlowData.operating.netCash >= 0 ? 'positive' : 'negative'}">${fmtAmount(cashFlowData.operating.netCash)}</td>
          </tr>

          <tr class="section-header"><td colspan="3">INVESTING ACTIVITIES</td></tr>
          ${buildInvestFinanceRows(cashFlowData.investing.items)}
          <tr class="total-row">
            <td>Net Cash from Investing</td>
            <td></td>
            <td class="amount ${cashFlowData.investing.netCash >= 0 ? 'positive' : 'negative'}">${fmtAmount(cashFlowData.investing.netCash)}</td>
          </tr>

          <tr class="section-header"><td colspan="3">FINANCING ACTIVITIES</td></tr>
          ${buildInvestFinanceRows(cashFlowData.financing.items)}
          <tr class="total-row">
            <td>Net Cash from Financing</td>
            <td></td>
            <td class="amount ${cashFlowData.financing.netCash >= 0 ? 'positive' : 'negative'}">${fmtAmount(cashFlowData.financing.netCash)}</td>
          </tr>

          <tr style="td{padding:8px;border:none}"><td colspan="3">&nbsp;</td></tr>

          <tr class="total-row">
            <td>Net Change in Cash</td>
            <td></td>
            <td class="amount ${cashFlowData.netChange >= 0 ? 'positive' : 'negative'}">${fmtAmount(cashFlowData.netChange)}</td>
          </tr>

          <tr class="grand-total">
            <td>Closing Cash & Bank Balance</td>
            <td></td>
            <td class="amount">${fmtAmount(cashFlowData.closingBalance)}</td>
          </tr>
        </tbody>
      </table>
      <div style="margin-top:24px;padding-top:12px;border-top:1px solid #eee;text-align:center;color:#999;font-size:11px">
        Generated on ${new Date().toLocaleString()} | ${activeCompany?.companyName || 'Company'}
      </div>
      <script>window.onload = function() { window.print(); }</script>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
  }, [activeCompany, dateFrom, dateTo, selectedFYId, financialYears, cashFlowData]);

  // ============================================================
  // CUSTOM TOOLTIPS
  // ============================================================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipBox>
          <TooltipLabel>{label}</TooltipLabel>
          {payload.map((entry, i) => (
            <TooltipRow key={i}>
              <TooltipDot style={{ background: entry.color || entry.stroke }} />
              <span>{entry.name}: {formatCurrency(entry.value)}</span>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  const WaterfallTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // payload[0] is base (invisible), payload[1] is value
      const val = payload.find(p => p.dataKey === 'value');
      return (
        <TooltipBox>
          <TooltipLabel>{label}</TooltipLabel>
          {val && (
            <TooltipRow>
              <span>{formatCurrency(val.value)}</span>
            </TooltipRow>
          )}
        </TooltipBox>
      );
    }
    return null;
  };

  // ============================================================
  // RENDER
  // ============================================================
  const selectedFY = financialYears.find(f => f.id === selectedFYId);
  const hasData = allTransactions.length > 0;
  const noTransactions = !loading && !error && hasData && cashFlowData.totalInflows === 0 && cashFlowData.totalOutflows === 0 && cashFlowData.openingBalance === 0;

  return (
    <PageLayout pageTitle="Cash Flow Statement" breadcrumb="Reports / Accounting Reports / Cash Flow">
      {/* Action Bar */}
      <ActionBar>
        <ActionBarLeft>
          <ActionButton onClick={() => setShowFilters(!showFilters)}>
            <FiFilter size={14} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </ActionButton>
        </ActionBarLeft>
        <ActionBarRight>
          <ActionButton onClick={handleRefresh} disabled={loading || !hasGenerated}>
            <FiRefreshCw size={14} className={loading ? 'spin' : ''} />
            Refresh
          </ActionButton>
          <ActionButton onClick={handleExport} disabled={loading || !hasData}>
            <FiDownload size={14} />
            Export CSV
          </ActionButton>
          <ActionButton onClick={handlePrint} disabled={loading || !hasData}>
            <FiPrinter size={14} />
            Print
          </ActionButton>
          <ActionButton $primary onClick={handleGenerateReport} disabled={loading || !selectedFYId || accounts.length === 0}>
            <FiBarChart2 size={14} />
            {loading ? 'Loading...' : 'Generate'}
          </ActionButton>
        </ActionBarRight>
      </ActionBar>

      {/* Filters */}
      {showFilters && (
        <FiltersCard>
          <FiltersRow>
            <FilterGroup>
              <Label><FiCalendar size={12} /> Financial Year</Label>
              <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
                <option value="">Select Financial Year</option>
                {financialYears.map(fy => (
                  <option key={fy.id} value={fy.id}>
                    {fy.yearName || fy.yearCode || fy.name || fy.id}
                    {fy.isActive ? ' (Active)' : ''}
                  </option>
                ))}
              </Select>
            </FilterGroup>
            <FilterGroup>
              <Label><FiCalendar size={12} /> Date From</Label>
              <DateInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </FilterGroup>
            <FilterGroup>
              <Label><FiCalendar size={12} /> Date To</Label>
              <DateInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </FilterGroup>
            <FilterGroup>
              <Label>View</Label>
              <Select value={viewMode} onChange={e => setViewMode(e.target.value)}>
                <option value="summary">Summary</option>
                <option value="detailed">Detailed</option>
              </Select>
            </FilterGroup>
            <FilterGroup>
              <Label>Period</Label>
              <Select value={periodType} onChange={e => setPeriodType(e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </Select>
            </FilterGroup>
          </FiltersRow>
        </FiltersCard>
      )}

      {/* Loading State */}
      {loading && (
        <EmptyState>
          <LoadingSpinner />
          <div style={{ marginTop: 16 }}>Loading Cash Flow data...</div>
        </EmptyState>
      )}

      {/* Error State */}
      {error && !loading && (
        <ErrorContainer>
          <FiActivity size={24} />
          <ErrorText>{error}</ErrorText>
          <ActionButton onClick={handleRefresh}>
            <FiRefreshCw size={14} />
            Retry
          </ActionButton>
        </ErrorContainer>
      )}

      {/* Not Generated Yet State */}
      {!loading && !error && !hasGenerated && accounts.length > 0 && (
        <EmptyState>
          <FiBarChart2 size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div>Select a financial year and click Generate to view the cash flow statement</div>
          <SubText>No data is loaded until you generate the report.</SubText>
        </EmptyState>
      )}

      {/* Empty State */}
      {!loading && !error && hasGenerated && !hasData && accounts.length > 0 && (
        <EmptyState>
          <FiActivity size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div>No cash/bank transactions found for the selected period</div>
          <SubText>Ensure transactions are posted and the financial year is selected.</SubText>
        </EmptyState>
      )}

      {/* No Accounts State */}
      {!loading && !error && accounts.length === 0 && (
        <EmptyState>
          <FiDollarSign size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div>No accounts found</div>
          <SubText>Set up your Chart of Accounts to generate cash flow reports.</SubText>
        </EmptyState>
      )}

      {/* Report Content */}
      {!loading && !error && hasData && (
        <>
          {/* Summary Cards */}
          <SummaryCards>
            <SummaryCard $borderColor={CHART_COLORS.primary}>
              <SummaryIcon $color={CHART_COLORS.primary}><FiDollarSign /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>Opening Cash Balance</SummaryLabel>
                <SummaryValue>{formatCurrency(cashFlowData.openingBalance)}</SummaryValue>
              </SummaryInfo>
            </SummaryCard>
            <SummaryCard $borderColor="#28A745">
              <SummaryIcon $color="#28A745"><FiTrendingUp /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>Total Inflows</SummaryLabel>
                <SummaryValue $color="#28A745">{formatCurrency(cashFlowData.totalInflows)}</SummaryValue>
              </SummaryInfo>
            </SummaryCard>
            <SummaryCard $borderColor="#DC3545">
              <SummaryIcon $color="#DC3545"><FiTrendingDown /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>Total Outflows</SummaryLabel>
                <SummaryValue $color="#DC3545">{formatCurrency(cashFlowData.totalOutflows)}</SummaryValue>
              </SummaryInfo>
            </SummaryCard>
            <SummaryCard $borderColor={CHART_COLORS.tertiary}>
              <SummaryIcon $color={CHART_COLORS.tertiary}><FiActivity /></SummaryIcon>
              <SummaryInfo>
                <SummaryLabel>Closing Cash Balance</SummaryLabel>
                <SummaryValue $color={CHART_COLORS.tertiary}>{formatCurrency(cashFlowData.closingBalance)}</SummaryValue>
              </SummaryInfo>
            </SummaryCard>
          </SummaryCards>

          {/* Cash Flow Statement Table */}
          <ContentCard>
            <ReportHeader>
              <ReportTitle>Cash Flow Statement</ReportTitle>
              <ReportMeta>
                {activeCompany?.companyName || ''} |{' '}
                {selectedFY?.yearName || selectedFY?.yearCode || selectedFY?.name || ''} |{' '}
                {fmtDate(dateFrom)} to {fmtDate(dateTo)} |{' '}
                {allTransactions.length} transactions
              </ReportMeta>
            </ReportHeader>

            <TableContainer>
              <Table>
                <thead>
                  <Tr>
                    <Th>Particulars</Th>
                    <Th $right style={{ width: 160 }}>Amount</Th>
                    <Th $right style={{ width: 160 }}>Net</Th>
                  </Tr>
                </thead>
                <tbody>
                  {/* Opening Balance */}
                  <GrandTotalRow>
                    <NameTd $grand>Opening Cash & Bank Balance</NameTd>
                    <AmountTd></AmountTd>
                    <AmountTd $bold $grand>{formatCurrency(cashFlowData.openingBalance)}</AmountTd>
                  </GrandTotalRow>

                  <SpacerRow><td colSpan="3"></td></SpacerRow>

                  {/* OPERATING ACTIVITIES */}
                  <SectionHeaderRow onClick={() => toggleSection('operating')}>
                    <SectionTd colSpan="3">
                      <SectionToggle>
                        {expandedSections.operating ? <FiChevronDown /> : <FiChevronRight />}
                      </SectionToggle>
                      OPERATING ACTIVITIES
                    </SectionTd>
                  </SectionHeaderRow>

                  {expandedSections.operating && (
                    <>
                      {/* Operating Inflows */}
                      {cashFlowData.operating.inflows.length > 0 && (
                        <>
                          <SubSectionRow>
                            <NameTd $subSection>Customer Receipts & Other Inflows</NameTd>
                            <AmountTd></AmountTd>
                            <AmountTd></AmountTd>
                          </SubSectionRow>
                          {cashFlowData.operating.inflows.map((item, idx) => (
                            <DetailRow key={`op-in-${idx}`}>
                              <NameTd $indent>{item.label}{viewMode === 'detailed' && ` (${item.count} txns)`}</NameTd>
                              <AmountTd $positive>{formatCurrency(item.amount)}</AmountTd>
                              <AmountTd></AmountTd>
                            </DetailRow>
                          ))}
                        </>
                      )}

                      {/* Operating Outflows */}
                      {cashFlowData.operating.outflows.length > 0 && (
                        <>
                          <SubSectionRow>
                            <NameTd $subSection>Vendor Payments & Other Outflows</NameTd>
                            <AmountTd></AmountTd>
                            <AmountTd></AmountTd>
                          </SubSectionRow>
                          {cashFlowData.operating.outflows.map((item, idx) => (
                            <DetailRow key={`op-out-${idx}`}>
                              <NameTd $indent>{item.label}{viewMode === 'detailed' && ` (${item.count} txns)`}</NameTd>
                              <AmountTd $negative>({formatCurrency(item.amount)})</AmountTd>
                              <AmountTd></AmountTd>
                            </DetailRow>
                          ))}
                        </>
                      )}

                      {cashFlowData.operating.inflows.length === 0 && cashFlowData.operating.outflows.length === 0 && (
                        <DetailRow>
                          <NameTd $indent $muted>No operating activities</NameTd>
                          <AmountTd></AmountTd>
                          <AmountTd></AmountTd>
                        </DetailRow>
                      )}
                    </>
                  )}

                  <TotalRow>
                    <NameTd $bold>Net Cash from Operations</NameTd>
                    <AmountTd></AmountTd>
                    <AmountTd $bold $positive={cashFlowData.operating.netCash >= 0} $negative={cashFlowData.operating.netCash < 0}>
                      {formatCurrency(cashFlowData.operating.netCash)}
                    </AmountTd>
                  </TotalRow>

                  <SpacerRow><td colSpan="3"></td></SpacerRow>

                  {/* INVESTING ACTIVITIES */}
                  <SectionHeaderRow onClick={() => toggleSection('investing')}>
                    <SectionTd colSpan="3">
                      <SectionToggle>
                        {expandedSections.investing ? <FiChevronDown /> : <FiChevronRight />}
                      </SectionToggle>
                      INVESTING ACTIVITIES
                    </SectionTd>
                  </SectionHeaderRow>

                  {expandedSections.investing && (
                    <>
                      {cashFlowData.investing.items.length > 0 ? (
                        cashFlowData.investing.items.map((item, idx) => (
                          <DetailRow key={`inv-${idx}`}>
                            <NameTd $indent>{item.label}{viewMode === 'detailed' && ` (${item.count} txns)`}</NameTd>
                            <AmountTd $positive={item.amount >= 0} $negative={item.amount < 0}>
                              {item.amount < 0 ? `(${formatCurrency(Math.abs(item.amount))})` : formatCurrency(item.amount)}
                            </AmountTd>
                            <AmountTd></AmountTd>
                          </DetailRow>
                        ))
                      ) : (
                        <DetailRow>
                          <NameTd $indent $muted>No investing activities</NameTd>
                          <AmountTd></AmountTd>
                          <AmountTd></AmountTd>
                        </DetailRow>
                      )}
                    </>
                  )}

                  <TotalRow>
                    <NameTd $bold>Net Cash from Investing</NameTd>
                    <AmountTd></AmountTd>
                    <AmountTd $bold $positive={cashFlowData.investing.netCash >= 0} $negative={cashFlowData.investing.netCash < 0}>
                      {formatCurrency(cashFlowData.investing.netCash)}
                    </AmountTd>
                  </TotalRow>

                  <SpacerRow><td colSpan="3"></td></SpacerRow>

                  {/* FINANCING ACTIVITIES */}
                  <SectionHeaderRow onClick={() => toggleSection('financing')}>
                    <SectionTd colSpan="3">
                      <SectionToggle>
                        {expandedSections.financing ? <FiChevronDown /> : <FiChevronRight />}
                      </SectionToggle>
                      FINANCING ACTIVITIES
                    </SectionTd>
                  </SectionHeaderRow>

                  {expandedSections.financing && (
                    <>
                      {cashFlowData.financing.items.length > 0 ? (
                        cashFlowData.financing.items.map((item, idx) => (
                          <DetailRow key={`fin-${idx}`}>
                            <NameTd $indent>{item.label}{viewMode === 'detailed' && ` (${item.count} txns)`}</NameTd>
                            <AmountTd $positive={item.amount >= 0} $negative={item.amount < 0}>
                              {item.amount < 0 ? `(${formatCurrency(Math.abs(item.amount))})` : formatCurrency(item.amount)}
                            </AmountTd>
                            <AmountTd></AmountTd>
                          </DetailRow>
                        ))
                      ) : (
                        <DetailRow>
                          <NameTd $indent $muted>No financing activities</NameTd>
                          <AmountTd></AmountTd>
                          <AmountTd></AmountTd>
                        </DetailRow>
                      )}
                    </>
                  )}

                  <TotalRow>
                    <NameTd $bold>Net Cash from Financing</NameTd>
                    <AmountTd></AmountTd>
                    <AmountTd $bold $positive={cashFlowData.financing.netCash >= 0} $negative={cashFlowData.financing.netCash < 0}>
                      {formatCurrency(cashFlowData.financing.netCash)}
                    </AmountTd>
                  </TotalRow>

                  <SpacerRow><td colSpan="3"></td></SpacerRow>

                  {/* Net Change */}
                  <NetChangeRow>
                    <NameTd $bold>Net Change in Cash</NameTd>
                    <AmountTd></AmountTd>
                    <AmountTd $bold $positive={cashFlowData.netChange >= 0} $negative={cashFlowData.netChange < 0}>
                      {formatCurrency(cashFlowData.netChange)}
                    </AmountTd>
                  </NetChangeRow>

                  {/* Closing Balance */}
                  <GrandTotalRow $final>
                    <NameTd $grand>Closing Cash & Bank Balance</NameTd>
                    <AmountTd></AmountTd>
                    <AmountTd $bold $grand>{formatCurrency(cashFlowData.closingBalance)}</AmountTd>
                  </GrandTotalRow>
                </tbody>
              </Table>
            </TableContainer>
          </ContentCard>

          {/* Detailed Transactions Table */}
          {viewMode === 'detailed' && cashFlowData.detailedItems.length > 0 && (
            <ContentCard style={{ marginTop: 20 }}>
              <ReportHeader>
                <ReportTitle>Detailed Cash Transactions</ReportTitle>
                <ReportMeta>
                  {cashFlowData.detailedItems.length} transactions
                </ReportMeta>
              </ReportHeader>
              <TableContainer>
                <Table>
                  <thead>
                    <Tr>
                      <Th style={{ width: 100 }}>Date</Th>
                      <Th style={{ width: 60 }}>Type</Th>
                      <Th>Description</Th>
                      <Th>Account</Th>
                      <Th $right style={{ width: 130 }}>Inflow</Th>
                      <Th $right style={{ width: 130 }}>Outflow</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {cashFlowData.detailedItems.map((item, idx) => (
                      <DetailRow key={item.id || idx}>
                        <CodeTd>{fmtDate(item.date)}</CodeTd>
                        <CodeTd>
                          <TranTypeBadge
                            $clickable={!!(item.transactionId || item.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToTransaction(navigate, item.tranTypeCode, item.transactionId || item.id);
                            }}
                            title={`Open ${item.tranTypeCode} transaction`}
                          >
                            {item.tranTypeCode} <FiExternalLink size={9} style={{ marginLeft: 2 }} />
                          </TranTypeBadge>
                        </CodeTd>
                        <NameTd>{item.description || '-'}</NameTd>
                        <NameTd $muted>{item.accountName}</NameTd>
                        <AmountTd $positive={item.inflow > 0}>
                          {item.inflow > 0 ? formatCurrency(item.inflow) : '-'}
                        </AmountTd>
                        <AmountTd $negative={item.outflow > 0}>
                          {item.outflow > 0 ? formatCurrency(item.outflow) : '-'}
                        </AmountTd>
                      </DetailRow>
                    ))}
                    <TotalRow>
                      <NameTd $bold colSpan="4">Total</NameTd>
                      <AmountTd $bold $positive>
                        {formatCurrency(cashFlowData.totalInflows)}
                      </AmountTd>
                      <AmountTd $bold $negative>
                        {formatCurrency(cashFlowData.totalOutflows)}
                      </AmountTd>
                    </TotalRow>
                  </tbody>
                </Table>
              </TableContainer>
            </ContentCard>
          )}

          {/* Period Comparison Table */}
          {cashFlowData.periodData.length > 0 && (
            <ContentCard style={{ marginTop: 20 }}>
              <ReportHeader>
                <ReportTitle>Period-wise Cash Flow Comparison</ReportTitle>
                <ReportMeta>
                  {periodType === 'monthly' ? 'Monthly' : 'Quarterly'} breakdown
                </ReportMeta>
              </ReportHeader>
              <TableContainer>
                <Table>
                  <thead>
                    <Tr>
                      <Th>Period</Th>
                      <Th $right>Inflows</Th>
                      <Th $right>Outflows</Th>
                      <Th $right>Net Flow</Th>
                      <Th $right>Closing Balance</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {cashFlowData.periodData.map((pd, idx) => (
                      <DetailRow key={idx}>
                        <NameTd $bold>{pd.period}</NameTd>
                        <AmountTd $positive={pd.inflows > 0}>
                          {formatCurrency(pd.inflows)}
                        </AmountTd>
                        <AmountTd $negative={pd.outflows > 0}>
                          {formatCurrency(pd.outflows)}
                        </AmountTd>
                        <AmountTd $positive={pd.netFlow >= 0} $negative={pd.netFlow < 0}>
                          {formatCurrency(pd.netFlow)}
                        </AmountTd>
                        <AmountTd $bold>{formatCurrency(pd.balance)}</AmountTd>
                      </DetailRow>
                    ))}
                    <TotalRow>
                      <NameTd $bold>Total</NameTd>
                      <AmountTd $bold $positive>
                        {formatCurrency(cashFlowData.periodData.reduce((s, p) => s + p.inflows, 0))}
                      </AmountTd>
                      <AmountTd $bold $negative>
                        {formatCurrency(cashFlowData.periodData.reduce((s, p) => s + p.outflows, 0))}
                      </AmountTd>
                      <AmountTd $bold $positive={cashFlowData.netChange >= 0} $negative={cashFlowData.netChange < 0}>
                        {formatCurrency(cashFlowData.netChange)}
                      </AmountTd>
                      <AmountTd $bold>{formatCurrency(cashFlowData.closingBalance)}</AmountTd>
                    </TotalRow>
                  </tbody>
                </Table>
              </TableContainer>
            </ContentCard>
          )}

          {/* Charts */}
          {cashFlowData.periodData.length > 0 && (
            <ChartsGrid>
              {/* Area Chart: Cash Balance Trend */}
              <ChartCard>
                <ChartTitle>Cash Balance Trend</ChartTitle>
                <ChartBody>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={balanceTrendData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatNumber(v, { compact: true })} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="balance"
                        name="Balance"
                        stroke={CHART_COLORS.primary}
                        strokeWidth={2}
                        fill="url(#balanceGradient)"
                        dot={{ r: 4, fill: CHART_COLORS.primary }}
                        activeDot={{ r: 6 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartBody>
              </ChartCard>

              {/* Bar Chart: Inflows vs Outflows */}
              <ChartCard>
                <ChartTitle>Inflows vs Outflows by Period</ChartTitle>
                <ChartBody>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={inflowOutflowData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatNumber(v, { compact: true })} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Inflows" fill="#28A745" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Outflows" fill="#DC3545" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartBody>
              </ChartCard>

              {/* Waterfall Chart: Cash Flow Movement */}
              <ChartCard $full>
                <ChartTitle>Cash Flow Waterfall</ChartTitle>
                <ChartBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={waterfallData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatNumber(v, { compact: true })} />
                      <Tooltip content={<WaterfallTooltip />} />
                      <Bar dataKey="base" stackId="stack" fill="transparent" />
                      <Bar dataKey="value" stackId="stack" radius={[4, 4, 0, 0]}>
                        {waterfallData.map((entry, index) => (
                          <rect key={index} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartBody>
              </ChartCard>
            </ChartsGrid>
          )}
        </>
      )}
    </PageLayout>
  );
};

// ============================================================
// HELPER: Transaction label from type code
// ============================================================
function getTransactionLabel(tranTypeCode, isInflow) {
  const labels = {
    CR: 'Customer Receipts',
    BR: 'Bank Receipts',
    CP: 'Cash Payments',
    BP: 'Bank Payments',
    SI: isInflow ? 'Sales Invoice (Cash)' : 'Sales Invoice (Refund)',
    NSI: isInflow ? 'Non-Tax Sales Invoice (Cash)' : 'Non-Tax Sales Invoice (Refund)',
    PI: isInflow ? 'Purchase Invoice (Refund)' : 'Purchase Invoice (Cash)',
    NPI: isInflow ? 'Non-Tax Purchase Invoice (Refund)' : 'Non-Tax Purchase Invoice (Cash)',
    SR: isInflow ? 'Sales Return (Refund)' : 'Sales Return (Cash)',
    NSR: isInflow ? 'Non-Tax Sales Return (Refund)' : 'Non-Tax Sales Return (Cash)',
    PR: isInflow ? 'Purchase Return (Cash)' : 'Purchase Return (Refund)',
    NPR: isInflow ? 'Non-Tax Purchase Return (Cash)' : 'Non-Tax Purchase Return (Refund)',
    JV: 'Journal Vouchers',
    FA: 'Fixed Assets',
    EQ: 'Equity / Capital',
    LN: 'Loans'
  };
  return labels[tranTypeCode] || `Other (${tranTypeCode})`;
}

// ============================================================
// STYLED COMPONENTS
// ============================================================

// Action Bar
const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const ActionBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  border-radius: ${colors.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 1px solid ${props => props.$primary ? colors.primary.main : colors.neutral[300]};
  background: ${props => props.$primary ? colors.primary.main : 'white'};
  color: ${props => props.$primary ? 'white' : colors.text.primary};

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? colors.primary.dark : colors.neutral[100]};
    border-color: ${props => props.$primary ? colors.primary.dark : colors.primary.main};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Filters
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
  flex: 1;
  max-width: 220px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

// Summary Cards
const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-left: 4px solid ${props => props.$borderColor || colors.primary.main};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${colors.shadows.md};
  }
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.$color || colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Charts
const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
  ${props => props.$full && 'grid-column: 1 / -1;'}
`;

const ChartTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ChartBody = styled.div`
  padding: 16px;
`;

// Tooltip
const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 12px;
  font-size: 12px;
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;
`;

const TooltipDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// Content & Table
const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const ReportHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const ReportTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const ReportMeta = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${colors.text.secondary};
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Tr = styled.tr``;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.primary.main};
  white-space: nowrap;
`;

const SectionHeaderRow = styled.tr`
  cursor: pointer;
  background: ${colors.primary.main}08;
  border-bottom: 2px solid ${colors.primary.main}30;
  &:hover { background: ${colors.primary.main}12; }
`;

const SectionTd = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 700;
  color: ${colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionToggle = styled.span`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${colors.primary.main};
`;

const SubSectionRow = styled.tr`
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const DetailRow = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-bottom: 1px solid ${colors.neutral[300]};
  border-top: 1px solid ${colors.neutral[300]};
`;

const NetChangeRow = styled.tr`
  background: ${colors.primary.main}12;
  border-top: 2px solid ${colors.primary.main};
  border-bottom: 2px solid ${colors.primary.main};
`;

const GrandTotalRow = styled.tr`
  background: ${props => props.$final ? colors.primary.main : `${colors.primary.main}15`};
  border-top: ${props => props.$final ? `3px solid ${colors.primary.dark}` : `2px solid ${colors.primary.main}`};
  border-bottom: ${props => props.$final ? `3px solid ${colors.primary.dark}` : `2px solid ${colors.primary.main}`};

  td {
    color: ${props => props.$final ? 'white' : colors.primary.main};
  }
`;

const SpacerRow = styled.tr`
  td { padding: 6px; border: none; background: transparent; }
`;

const CodeTd = styled.td`
  padding: 10px 16px;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  color: ${colors.text.secondary};
  white-space: nowrap;
`;

const NameTd = styled.td`
  padding: 10px 16px;
  font-size: 13px;
  color: ${props => {
    if (props.$muted) return colors.text.tertiary;
    if (props.$grand) return 'inherit';
    return colors.text.primary;
  }};
  font-weight: ${props => (props.$bold || props.$grand || props.$subSection) ? 600 : 400};
  font-style: ${props => props.$muted ? 'italic' : 'normal'};
  padding-left: ${props => props.$indent ? '40px' : props.$subSection ? '24px' : '16px'};
  font-size: ${props => props.$grand ? '14px' : props.$subSection ? '12px' : '13px'};
  text-transform: ${props => props.$subSection ? 'uppercase' : 'none'};
  letter-spacing: ${props => props.$subSection ? '0.3px' : 'normal'};
  color: ${props => props.$subSection ? colors.text.secondary : props.$muted ? colors.text.tertiary : props.$grand ? 'inherit' : colors.text.primary};
`;

const AmountTd = styled.td`
  padding: 10px 16px;
  text-align: right;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  font-weight: ${props => props.$bold ? 600 : 400};
  color: ${props => {
    if (props.$positive) return '#28A745';
    if (props.$negative) return '#DC3545';
    if (props.$grand) return 'inherit';
    return colors.text.primary;
  }};
  white-space: nowrap;
  font-size: ${props => props.$grand ? '14px' : '13px'};
`;

const TranTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;

  ${props => props.$clickable && `
    &:hover {
      background: ${colors.primary.main}30;
      text-decoration: underline;
    }
  `}
`;

// Loading & Empty states
const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
  font-size: 14px;
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
`;

const SubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
  margin-top: 8px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  color: ${colors.error.main};
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: ${colors.error.main};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default CashFlow;
